import React, { useEffect, useState } from "react";
import { I } from "../components/Icons";
import { clsx } from "../components/Primitives";
import { queueSize, onQueueChange } from "../api/client";

/** Phone layout: a slim header, the screen, and a bottom bar of five tabs with the capture button in the middle. */

export type PhoneTab = "today" | "tasks" | "inbox" | "more";

export const PHONE_TITLES: Record<string, string> = {
  today: "Today", tasks: "Tasks", inbox: "Inbox", "inbox-all": "All email", contacts: "Contacts", clients: "Accounts", accounts: "Accounts",
  pipeline: "Pipeline", "pipeline-board": "Pipeline", project: "Deal", radar: "Radar", leads: "Leads", newsletters: "Newsletters",
  files: "Files", settings: "Settings", principal: "Principal", principals: "Principals", countries: "Markets", more: "More",
  notifications: "Notifications", web: "Web", install: "Install", targets: "Relationship targets",
};
/** Which tab a screen belongs to (everything not on a tab lives under More). */
export const tabOf = (screen: string): PhoneTab =>
  screen === "today" ? "today" : screen === "tasks" ? "tasks" : screen === "inbox" || screen === "inbox-all" ? "inbox" : "more";
const TOP_LEVEL = new Set(["today", "tasks", "inbox", "more"]);

export default function PhoneShell({ screen, go, badges, onSearch, onAsk, onCapture, children }: {
  screen: string;
  go: (screen: string, id?: string | null) => void;
  badges: { tasks: number; inbox: number; today: number };
  onSearch: () => void;
  onAsk: () => void;
  onCapture: (kind: "minutes" | "task" | "contact" | "paste") => void;
  children: React.ReactNode;
}) {
  const [capture, setCapture] = useState(false);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [queued, setQueued] = useState(queueSize());
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    const unsub = onQueueChange(setQueued);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); unsub(); };
  }, []);
  const tab = tabOf(screen);
  const sub = !TOP_LEVEL.has(screen);
  const pick = (k: "minutes" | "task" | "contact" | "paste") => { setCapture(false); onCapture(k); };

  return (
    <div className="ph">
      <header className="ph-head">
        {sub ? (
          <button className="ph-back" onClick={() => (window.history.length > 1 ? window.history.back() : go("more"))} aria-label="Back">‹ <span>Back</span></button>
        ) : <span className="ph-brand"><img src="/favicon.png" alt="" />Saga</span>}
        <h1 className="ph-title">{PHONE_TITLES[screen] || "Saga"}</h1>
        <button className="ph-icon" onClick={onSearch} aria-label="Search">{I.search({ size: 19 })}</button>
        <button className="ph-icon" onClick={onAsk} aria-label="Ask Saga">{I.ai({ size: 19 })}</button>
      </header>
      {(!online || queued > 0) && (
        <div className={clsx("ph-offline", online && "syncing")}>
          {online ? `Sending ${queued} change${queued === 1 ? "" : "s"} made offline…` : `Offline — showing what was last loaded${queued ? ` · ${queued} change${queued === 1 ? "" : "s"} will be sent when you're back online` : ""}`}
        </div>
      )}
      <main className="ph-main">{children}</main>

      <nav className="ph-tabs" aria-label="Main">
        <TabBtn label="Today" on={tab === "today"} badge={badges.today} onClick={() => go("today")} icon={I.home} />
        <TabBtn label="Tasks" on={tab === "tasks"} badge={badges.tasks} onClick={() => go("tasks")} icon={I.tasks} />
        <button className="ph-fab" aria-label="Capture" onClick={() => setCapture(true)}>＋</button>
        <TabBtn label="Inbox" on={tab === "inbox"} badge={badges.inbox} onClick={() => go("inbox")} icon={I.mail} />
        <TabBtn label="More" on={tab === "more"} onClick={() => go("more")} icon={I.sliders} />
      </nav>

      {capture && (
        <div className="ph-sheet-wrap" onClick={() => setCapture(false)}>
          <div className="ph-sheet" role="dialog" aria-label="Capture" onClick={e => e.stopPropagation()}>
            <div className="ph-grab" />
            <div className="ph-sheet-title">Capture</div>
            <button className="ph-cap-main" onClick={() => pick("minutes")}>
              <span className="ph-mic">●</span>
              <span><b>Minutes</b>Type or dictate with the keyboard's microphone — Saga writes the minutes and suggests tasks</span>
            </button>
            <div className="ph-cap-grid">
              <button onClick={() => pick("task")}><b>Task</b>for you or a colleague</button>
              <button onClick={() => pick("contact")}><b>Contact</b>someone you just met</button>
              <button onClick={() => pick("paste")}><b>Paste</b>any text — Saga files it</button>
              <button onClick={() => { setCapture(false); go("tasks"); }}><b>From notes</b>tasks from a pasted list</button>
            </div>
            <button className="ph-btn ghost" onClick={() => setCapture(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ label, on, badge, onClick, icon }: {
  label: string; on: boolean; badge?: number; onClick: () => void; icon: (p: { size?: number; w?: number }) => React.ReactNode;
}) {
  return (
    <button className={clsx("ph-tab", on && "on")} onClick={onClick} aria-current={on ? "page" : undefined}>
      <span className="ph-tab-ic">{icon({ size: 22, w: on ? 2 : 1.7 })}{!!badge && <i className="ph-badge">{badge > 99 ? "99+" : badge}</i>}</span>
      {label}
    </button>
  );
}
